/**
 * Shared meter/offer calculation utilities
 *
 * Used by both EA Report Preview and Offer Comparison to ensure
 * Total KWH (consumption) and Contract Cost ($) are calculated consistently.
 *
 * Consumption formula: dailyConsumption × periodDays × (contractType === TARIFF ? 1 : dlf × mlf)
 * i.e. per-meter loss factor for non-TARIFF (C&I) sites.
 *
 * - Input: meter → calculates consumption (period)
 * - Input: meter + period → calculates period consumption
 * - Input: selections + meters + getPeriod → calculates total period consumption (整个period)
 */

import { DAYS_PER_YEAR } from '~/constants/date';
import { calculateDailyEnergy } from '~/models/common';
import { SiteClassification } from '~/models/meter';
import { ReportProjectMeter } from './reportTypes';

export interface ConsumptionOptions {
  /** When true, use per-meter dlf×mlf (and contractType) for consumption. Preferred. */
  usePerMeterLossFactor?: boolean;
  /** Weighted average DLF - used only when usePerMeterLossFactor is false (legacy) */
  lwaDLF?: number;
  /** Weighted average MLF - used only when usePerMeterLossFactor is false (legacy) */
  lwaMLF?: number;
  /** If true, do not apply loss factor (TARIFF sites). Used when usePerMeterLossFactor is false. */
  isTariff?: boolean;
}

/**
 * Calculate meter consumption (Total KWH) for a given period length.
 * Formula: dailyConsumption × numOfDays × (contractType === TARIFF ? 1 : dlf × mlf)
 *
 * When usePerMeterLossFactor is true (default for new code): uses meter's own dlf, mlf and contractType.
 * Otherwise uses lwaDLF×lwaMLF and isTariff from options (legacy).
 */
export function calculateMeterConsumption(
  meter: ReportProjectMeter,
  numOfDays: number,
  options?: ConsumptionOptions
): number {
  const originalTotalEnergy = meter.annualData?.totalEnergy ?? 0;
  const dailyTotalEnergy = calculateDailyEnergy(originalTotalEnergy);
  let consumption = dailyTotalEnergy * (numOfDays || 0);

  if (options?.usePerMeterLossFactor) {
    const isTariff = meter.contract?.contractType === SiteClassification.TARIFF;
    if (!isTariff) {
      const dlf = meter.dlf ?? meter.tlf ?? 1;
      const mlf = meter.mlf ?? meter.tlf ?? 1;
      consumption *= dlf * mlf;
    }
  } else if (options?.lwaDLF != null && options?.lwaMLF != null && !options?.isTariff) {
    consumption *= options.lwaDLF * options.lwaMLF;
  }

  // Round to fixed precision so EA Report and Offer Comparison show identical Total KWH (avoid float drift)
  return Math.round(consumption * 1e4) / 1e4;
}

/** Same formula as calculateMeterConsumption but returns peak/offPeak/shoulder/total for EA Report Bill display */
export function calculateMeterConsumptionByComponent(
  meter: ReportProjectMeter,
  numOfDays: number,
  options?: ConsumptionOptions
): { peak: number; offPeak: number; shoulder: number; total: number } {
  const dailyPeak = calculateDailyEnergy(meter.annualData?.peakEnergy ?? 0);
  const dailyOffPeak = calculateDailyEnergy(meter.annualData?.offPeakEnergy ?? 0);
  const dailyShoulder = calculateDailyEnergy(meter.annualData?.shoulderEnergy ?? 0);
  let mult = 1;
  if (options?.usePerMeterLossFactor) {
    const isTariff = meter.contract?.contractType === SiteClassification.TARIFF;
    if (!isTariff) {
      const dlf = meter.dlf ?? meter.tlf ?? 1;
      const mlf = meter.mlf ?? meter.tlf ?? 1;
      mult = dlf * mlf;
    }
  } else if (options?.lwaDLF != null && options?.lwaMLF != null && !options?.isTariff) {
    mult = options.lwaDLF * options.lwaMLF;
  }
  const peak = Math.round(dailyPeak * (numOfDays || 0) * mult * 1e4) / 1e4;
  const offPeak = Math.round(dailyOffPeak * (numOfDays || 0) * mult * 1e4) / 1e4;
  const shoulder = Math.round(dailyShoulder * (numOfDays || 0) * mult * 1e4) / 1e4;
  const total = calculateMeterConsumption(meter, numOfDays, options);
  return { peak, offPeak, shoulder, total };
}

export interface PeriodLike {
  /** Segment days (single segment); matches cost calculation */
  numOfDays?: number;
  /** Aggregated period days = sum(segment numOfDays); use when set to avoid using first-segment numOfDays */
  totalNumOfDays?: number;
}

/**
 * Calculate meter period consumption - wrapper for calculateMeterConsumption.
 * Uses period.numOfDays (same as cost); for aggregated periods use totalNumOfDays when set.
 */
export function calculateMeterPeriodConsumption(
  meter: ReportProjectMeter,
  period: PeriodLike | undefined,
  options?: ConsumptionOptions
): number {
  // Prefer totalNumOfDays when set (aggregated period = sum of segment numOfDays); else numOfDays (segment)
  const days = period?.totalNumOfDays ?? period?.numOfDays ?? 0;
  return calculateMeterConsumption(meter, days, options);
}

export interface SelectionConsumptionResult {
  /** Per-meter period consumption (Total KWH for each meter) */
  consumptionByMeter: Record<string, number>;
  /** Total period consumption = sum of all meters' period consumption (整个period) */
  totalPeriodConsumption: number;
  /** Total annual consumption (for allocating currentContestableCost proportionally) */
  totalAnnualConsumption: number;
}

export interface MeterSelectionLike {
  meterId: string;
}

/**
 * Calculate consumption for all selections in a combination.
 * Uses per-meter formula: dailyConsumption × periodDays × (TARIFF ? 1 : dlf × mlf).
 */
export function calculateSelectionsConsumption<T extends MeterSelectionLike>(
  selections: T[],
  meters: ReportProjectMeter[],
  getPeriod: (selection: T) => PeriodLike | undefined,
  options?: ConsumptionOptions
): SelectionConsumptionResult {
  const consumptionByMeter: Record<string, number> = {};
  let totalPeriodConsumption = 0;
  let totalAnnualConsumption = 0;

  selections.forEach((selection) => {
    const meter = meters.find((m) => m.id === selection.meterId);
    const period = getPeriod(selection);
    const consumptionOpts: ConsumptionOptions = {
      ...options,
      usePerMeterLossFactor: options?.usePerMeterLossFactor ?? true,
      isTariff: meter?.contract?.contractType === SiteClassification.TARIFF
    };
    const periodConsumption = meter && period ? calculateMeterPeriodConsumption(meter, period, consumptionOpts) : 0;

    consumptionByMeter[selection.meterId] = periodConsumption;
    totalPeriodConsumption += periodConsumption;

    const periodDays = period?.totalNumOfDays ?? period?.numOfDays ?? 0;
    if (periodConsumption > 0 && periodDays > 0) {
      totalAnnualConsumption += periodConsumption * (DAYS_PER_YEAR / periodDays);
    }
  });

  return {
    consumptionByMeter,
    totalPeriodConsumption,
    totalAnnualConsumption
  };
}
